
import { useState, useEffect, useRef } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import moment from 'moment';
import { Search, Check, Copy, ExternalLink, ArrowLeft } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { logout } from '../../hooks/logout';
import PermissionDenied from '../../pages/common/permissionDenied';
import { WaitingLoader } from '../../hooks/buttonLoad';
import { useGetAdminFeeTransaction } from './api/adminFeeApi';
import { NoRecords } from '../../lib/loader';
import { useDebounce } from '../../hooks/debounce';
import { useModalContext } from '../../contexts/ModalContext';
import { copyToClipboard } from '../../lib/copytoClipboard';
import { useToast } from '../../hooks/usetoast';
import { useCheckAdminAccess } from '../WhiteIp/api/whiteipapi';
import { useBreadcrumbPagination } from '../../hooks/useBreadcrumbPagination';
import BreadcrumbPagination from '../../utils/BreadcrumbPagination';
import { formatAddress } from '../../utils/formatAddress';

function AdminFeeHistory() {

    const [tempPage, setTempPage] = useState(0);
    const [tempRows, setTempRows] = useState(25);
    const [search, setSearch] = useState("");
    const { theme } = useModalContext();
    const toast = useToast();
    const searchText = useDebounce(search);
    const [fromEmail, setFromEmail] = useState(false);
    const [copy, setCopy] = useState(false);
    const [double, setDouble] = useState(false);
    const location = useLocation();
    const { currency, network } = location.state || {};
    const { data: getTransactionHistory, isLoading } = useGetAdminFeeTransaction({
        page: searchText ? 0 : tempPage,
        size: tempRows,
        sortOrder: 'desc',
        sortField: 'createdAt',
        searchText,
        currency,
        network
    });

    const totalRecords = getTransactionHistory?.data?.totalRecords || 0;

    const {
        page,
        rowsPerPage,
        totalPages,
        handleChangePage,
        handleChangeRowsPerPage
    } = useBreadcrumbPagination(totalRecords, tempRows);

    useEffect(() => {
        setTempPage(page);
    }, [page]);

    useEffect(() => {
        setTempRows(rowsPerPage);
    }, [rowsPerPage]);

    useEffect(() => {
        setTempPage(0);
        if (handleChangePage) {
            handleChangePage(null, 0);
        }
    }, [searchText]);


    const inputRef = useRef(null);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
            const length = inputRef.current.value.length;
            inputRef.current.setSelectionRange(length, length);
        }
    });


    const copyUserEmailAddress = async data => {
        setFromEmail(data._id)
        await copyToClipboard(data?.senderEmail)
        toast.success(" From Address copied")
        setTimeout(() => {
            setFromEmail()
        }, 800)
    }

    const copyAddress = async data => {
        setCopy(data._id)
        await copyToClipboard(data.address)
        toast.success("Address copied")
        setTimeout(() => {
            setCopy()
        }, 800)
    }

    const copytxnhashAddress = async data => {
        setDouble(data._id)
        await copyToClipboard(data.txnHash)
        toast.success("Transaction Hash copied")
        setTimeout(() => {
            setDouble()
        }, 800)
    }


    const checkAdminMutation = useCheckAdminAccess();
    const [access, setAccess] = useState(true);

    useEffect(() => {
        checkAdminAccess();
    }, []);


    const checkAdminAccess = async () => {
        try {
            const obj = { page: 'History' };
            const checkAdminRes = await checkAdminMutation.mutateAsync(obj);
            if (!checkAdminRes?.data?.status) {
                setAccess(false);
                logout();
            } else {
                setAccess(true);
            }
        } catch (error) {
            setAccess(false);
        }
    };

    const renderCopyIcon = (isCopied, onClick, theme) => {
        return isCopied ? (
            <Check size={20} style={{ marginLeft: "5px", color: theme ? "white" : "black" }} />
        ) : (
            <Copy onClick={onClick} size={20} className="ml-5" style={{ cursor: "pointer", marginLeft: "5px", color: theme ? "white" : "black" }} />
        );
    };

    const renderTransactionLink = (hash, urlPrefix, isCopied, onClickCopy, theme) => {
        return hash ? (
            <>
                {`${hash.slice(0, 3)}...${hash.slice(-3)}`}
                {renderCopyIcon(isCopied, onClickCopy, theme)}
                <a href={urlPrefix} target="_blank" rel="noopener noreferrer" className="ml-2">
                    <ExternalLink style={{ marginLeft: "5px" }} size={20} />
                </a>
            </>
        ) : (
            "---"
        );
    };


    const FeeHistoryTable = () => (
        getTransactionHistory?.data?.transactionHistory?.length > 0 ? (
            <>
                <div style={{ minHeight: "60vh" }}>
                    <div className='table-responsive'>
                        <Table sx={{ minWidth: 650 }} aria-label="internal transaction history table">
                            <TableHead>
                                <TableRow>
                                    <TableCell className='text-nowrap' align="center">S.NO</TableCell>
                                    <TableCell className='text-nowrap' align="center">Email</TableCell>
                                    <TableCell className='text-nowrap' align="center">Address</TableCell>
                                    <TableCell className='text-nowrap' align="center">Fee</TableCell>
                                    <TableCell className='text-nowrap' align="center">Currency</TableCell>
                                    <TableCell className='text-nowrap' align="center">TXN Hash</TableCell>
                                    <TableCell className='text-nowrap' align="center">Status</TableCell>
                                    <TableCell className='text-nowrap' align="center">
                                        Date & Time{' '}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {getTransactionHistory?.data?.transactionHistory?.map((row, i) => (
                                    <TableRow key={row?._id || i}>
                                        <TableCell className='text-nowrap' align="center">{page * rowsPerPage + (i + 1)}</TableCell>
                                        <TableCell align="center">
                                            {row?.senderEmail || "-"}
                                            {fromEmail === row._id ? (
                                                <Check size={20} style={{ marginLeft: "5px", color: theme ? "white" : 'black' }} />
                                            ) : (
                                                <Copy
                                                    onClick={() => copyUserEmailAddress(row)}
                                                    size={20}
                                                    className='ml-5'
                                                    style={{ cursor: "pointer", marginLeft: "5px", color: theme ? "white" : 'black' }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            {formatAddress(row?.address || "-")}
                                            {renderCopyIcon(copy === row._id, () => copyAddress(row), theme)}
                                        </TableCell>

                                        <TableCell className="text-nowrap" align="center">
                                            {row?.fee ? (
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                    {row.fee}
                                                    {row?.currencyImage && (
                                                        <img
                                                            src={row.currencyImage}
                                                            alt="Currency Icon"
                                                            style={{
                                                                height: '20px',
                                                                width: '20px',
                                                                borderRadius: '50%',
                                                                verticalAlign: 'middle',
                                                                objectFit: 'cover',
                                                            }}
                                                        />
                                                    )}
                                                </span>
                                            ) : (
                                                '-'
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            {row?.currencySymbol}
                                        </TableCell>
                                        <TableCell align="center">
                                            {renderTransactionLink(row.admin_trans_txnHash, row.explorerUrl, double === row._id, () => copytxnhashAddress(row), theme)}
                                        </TableCell>

                                        <TableCell className="text-nowrap" align="center">
                                            {(() => {
                                                let statusLabel;
                                                if (row.withdrawStatus === 'Completed') {
                                                    statusLabel = <span className="text-success">Completed</span>;
                                                } else if (row.withdrawStatus === 'Approved') {
                                                    statusLabel = <span className="text-success">Approved</span>;
                                                } else {
                                                    statusLabel = <span className="text-danger">Rejected</span>;
                                                }
                                                return statusLabel;
                                            })()}
                                        </TableCell>

                                        <TableCell className='text-nowrap' align="center">
                                            {moment(row?.createdAt).format("DD-MM-YYYY | hh:mm A")}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </div>
                <BreadcrumbPagination
                    page={page}
                    rowsPerPage={rowsPerPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                />
            </>
        ) : (
            <NoRecords />
        )
    );

    let content;

    if (isLoading) {
        content = <WaitingLoader />;
    } else if (access) {
        const tableContent = FeeHistoryTable();

        content = (
            <section>
                <h4>{network} {currency} Fee History</h4>
                <div className="ad_pg_bx bg-white p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="col-md-4">
                            <div className="input-group">
                                <span className="input-group-icon">
                                    <Search size={20} color="#aaa9a9" />
                                </span>
                                <input
                                    type="text"
                                    ref={inputRef}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="form-control"
                                    value={search}
                                    placeholder="Search Email"
                                />
                            </div>
                        </div>
                        <div className="ms-3">
                            <button
                                className="btn btn-primary d-flex align-items-center"
                                onClick={() => window.history.back()}
                            >
                                <ArrowLeft size={18} className="me-2" /> Back
                            </button>
                        </div>
                    </div>
                    {tableContent}
                </div>
            </section>
        );
    } else {
        content = <PermissionDenied />;
    }

    return <>{content}</>;

}

export default AdminFeeHistory;